/**
 * MAE/MFE excursion. Phase 1, module 3 of 4 — and the one carrying the most
 * paid-for corrections.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` `computeExcursionWalk` —
 * semantics, not code (D3). Everything after the bars are fetched: contract-roll
 * correction, the walk, the realized-P&L invariant clamp, time-to-extreme.
 * Fetching stays in `io/`; this is pure.
 *
 * Each fix below is labelled with the audit id it came from (F4, F5, …). They
 * are not stylistic — every one is a wrong number somebody already found and
 * paid to fix. **Removing one reintroduces a bug that will look plausible.**
 */

/** An OHLC bar. `ts` is epoch ms. */
export interface Bar {
  ts: number;
  o: number;
  h: number;
  l: number;
  c: number;
}

/**
 * Plausible per-instrument roll/contango gap, as a fraction of price.
 *
 * Equity index carries ~1.5%/quarter, so 3% is generous. Crude gets 10% for its
 * steeper and more variable term structure. Anything beyond the cap is data
 * corruption, not a roll — and the right response is to skip rather than
 * fabricate a shifted price series.
 */
export const ROLL_OFFSET_MAX_FRAC: Readonly<Record<string, number>> = {
  ES: 0.03, NQ: 0.03, YM: 0.03, RTY: 0.03,
  MES: 0.03, MNQ: 0.03, MYM: 0.03, M2K: 0.03,
  CL: 0.10,
  GC: 0.03,
};

/** Cap for an instrument with no explicit entry. */
export const DEFAULT_ROLL_MAX_FRAC = 0.06;

export function rollMaxFrac(symbol: string): number {
  return ROLL_OFFSET_MAX_FRAC[symbol] ?? DEFAULT_ROLL_MAX_FRAC;
}

/**
 * Is a roll offset beyond the plausible cap for this instrument?
 *
 * Non-finite offset or non-positive price → NOT exceeded: there is nothing to
 * reject, and a cap cannot be scaled off a price of zero.
 */
export function rollOffsetExceedsCap(offset: number, price: number, symbol: string): boolean {
  if (!isFinite(offset) || !isFinite(price) || price <= 0) return false;
  return Math.abs(offset) > price * rollMaxFrac(symbol);
}

export type TradeSideish = 'long' | 'short';

export interface WalkParams {
  entry: number;
  side: TradeSideish;
  exit: number | null;
  /** True when `timeOut` was unreliable and the window is a 5-minute proxy. */
  estimatedEnd: boolean;
  startMs: number;
  endMs: number;
  /** True when the bars are the EXACT contract traded, not a continuous proxy. */
  isExact: boolean;
  symbol: string;
  tz: string;
}

export interface WalkSuccess {
  maePoints: number;
  mfePoints: number;
  /**
   * Seconds from entry to the observed extreme — or `null` when the clamp below
   * replaced the POINTS, because the clamped value was never seen in the bars
   * and so has no time at which it happened. See the return statement.
   */
  maeSecs: number | null;
  mfeSecs: number | null;
  excursionTz: string;
  /** F6 — true when the invariant clamp MATERIALLY moved a value. */
  clamped: boolean;
  clampInfo: { mfe: number; mae: number } | null;
  bars: number;
  rollOffsetApplied: number;
}

export interface WalkFailure {
  error: string;
  errorCode: 'CONTRACT_MISMATCH' | 'NO_BARS';
  offset?: number;
  gEntry?: number;
  gExit?: number | null;
}

export type WalkResult = WalkSuccess | WalkFailure;

export const isWalkFailure = (r: WalkResult): r is WalkFailure => 'errorCode' in r;

/** Clamps below this are float noise, not a real flooring. */
const CLAMP_EPSILON = 1e-6;

/**
 * Strip binary-float noise from a points figure (F9).
 *
 * 🚨 MEASURED 2026-08-19: a short at entry 100 against a bar high of 100.1
 * returns `maePoints: 0.09999999999999432`. `entry - low` is exact arithmetic on
 * values neither of which is representable in binary floating point, so the
 * result carries the error of both. It reaches the database and every consumer
 * that does not format it — a CSV export, a tooltip, an AI-coach prompt, a
 * copied cell — and it breaks any comparison against a tick multiple.
 *
 * ⛔ RULED OUT: snapping to the instrument's tick size. It is the obvious fix
 * and it is WRONG HERE, twice over:
 *   · in proxy mode the bars are shifted by a computed roll `offset`, which is
 *     an average of two anchors and is NOT tick-aligned. Snapping would move a
 *     genuine value to make it look tidy.
 *   · the tick is not a parameter of this function, so honouring it would mean
 *     threading `ContractSpec` through a pure walk — and beta's copy has no such
 *     argument, so the two clients would diverge on a number that must match.
 *
 * ▶ Rounding at 6 dp removes the artefact WITHOUT touching any real value: the
 * finest tick in the catalogue is ZB's 1/32 = 0.03125, five decimals, and bar
 * extremes are themselves quoted in ticks — so after the noise is gone a
 * genuine figure is already a tick multiple. This corrects the arithmetic; it
 * does not editorialise about the price.
 */
const POINTS_DP = 6;
const roundPoints = (n: number): number =>
  Number.isFinite(n) ? Math.round(n * 10 ** POINTS_DP) / 10 ** POINTS_DP : n;

/**
 * The MAE/MFE walk.
 *
 * @param inWindow bars already trimmed to the hold window (padding stripped)
 */
/**
 * Is every OHLC field of this bar a usable price?
 *
 * 🚨 A BAR WITH A ZERO OR MISSING PRICE IS THE SINGLE MOST EXPENSIVE INPUT THIS
 * FUNCTION CAN RECEIVE, because `maeRaw = entry - low` turns a zero low into an
 * MAE of the ENTIRE ENTRY PRICE. Measured 2026-08-19: a low of `0` on a 5000
 * entry returned `maePoints: 5000` with `clamped: false` — finite, so it stored
 * and rendered like any real number. On MES that is a $25,000 adverse excursion
 * printed against a trade that made $10, and it outranks every genuine MAE in
 * the book.
 *
 * ⚠ `null` IS THE LIKELIER SHAPE, NOT `0`. Bars arrive as parsed JSON through
 * `io/`, which validates nothing, and `null - 0` is `0` in JS — so a missing
 * field becomes a price of zero silently. The `Bar` interface types these as
 * `number`, but a wire boundary makes that a claim about the data rather than a
 * check on it.
 *
 * ⚠ AND THE REALIZED-P&L CLAMP CANNOT SAVE IT: that clamp only floors values
 * UPWARD. Nothing in this pipeline ever asked whether a number was too BIG.
 *
 * This is the same hazard F11 already guards for the exit price ("an exit of 0
 * would floor MAE up to the entire entry price"). The bar path never got the
 * matching guard, which is how it survived the port into this client intact.
 */
const usableBar = (b: Bar): boolean =>
  Number.isFinite(b.o) && b.o > 0 && Number.isFinite(b.h) && b.h > 0
  && Number.isFinite(b.l) && b.l > 0 && Number.isFinite(b.c) && b.c > 0;

export function computeExcursionWalk(inWindow: readonly Bar[], p: WalkParams): WalkResult {
  if (!inWindow.length) {
    return { error: 'No bars in the hold window.', errorCode: 'NO_BARS' };
  }

  // Drop unusable bars BEFORE anchoring or walking — see `usableBar`. Dropping
  // the bar rather than failing the trade is deliberate: one bad tick in a
  // window of hundreds should cost that tick, not the whole excursion. If it
  // empties the window we fall through to the same NO_BARS refusal as an empty
  // input, because an excursion computed from nothing is exactly the fabricated
  // number this guard exists to prevent.
  // F7: ENFORCE THE WINDOW HERE RATHER THAN TRUSTING THE CALLER.
  //
  // MEASURED 2026-08-19: a bar at T0+600s with `endMs = T0+60000` returned
  // `mfeSecs: 600` for a stated 60-second window — no error, no flag. A
  // time-to-extreme longer than the trade itself, which is the same impossible
  // duration the ATAS logger needed a guard for (D188).
  //
  // 🔴 AND THIS FUNCTION HAS NO PRODUCTION CALLER IN THIS CLIENT YET — grep
  // finds only tests. So "the caller filters first" is not a convention being
  // preserved here, it is a convention that does not exist yet. Putting the
  // invariant inside means the first caller cannot get it wrong.
  //
  // ⚠ THE PREDICATE IS COPIED FROM WHAT SHIPS, NOT INVENTED. Beta filters
  // `b.ts >= startMs && b.ts <= endMs` — inclusive both ends, on the bar's own
  // stamp. Choosing a different rule here (say, keeping the bar whose open
  // precedes the entry but whose range covers it) would be defensible in the
  // abstract and would silently make the two clients disagree on a stored
  // number. Parity first; if the rule is wrong it is wrong in both places and
  // gets changed in both.
  const windowed = (Number.isFinite(p.startMs) && Number.isFinite(p.endMs))
    ? inWindow.filter((b) => b.ts >= p.startMs && b.ts <= p.endMs)
    : inWindow;
  if (!windowed.length) {
    return {
      error: `No bars inside the hold window (${inWindow.length} supplied, all outside `
           + `${new Date(p.startMs).toISOString()}–${new Date(p.endMs).toISOString()}).`,
      errorCode: 'NO_BARS',
    };
  }

  const usable = windowed.filter(usableBar);
  if (!usable.length) {
    return {
      error: `No usable bars in the hold window (${windowed.length} had a zero or missing price).`,
      errorCode: 'NO_BARS',
    };
  }

  // F4: guarantee chronological order before anchoring or walking. The roll
  // anchor (bars[0]) and time-to-extreme (ts − startMs) both assume ascending
  // ts. Nothing enforced it here, and ONE out-of-order response would silently
  // corrupt the offset for every bar. Sort a COPY — never mutate the caller's
  // cached array.
  const bars = usable.slice().sort((a, b) => (a.ts || 0) - (b.ts || 0));
  const first = bars[0] as Bar;
  const last = bars[bars.length - 1] as Bar;

  // ── Contract-roll correction ──────────────────────────────────────────────
  // The bars feed serves a Calendar-rolled CONTINUOUS symbol. Near a quarterly
  // expiry it rolls to the next contract AHEAD of the real front month, so bars
  // sit a quarter of contango above the price actually traded. The gap G is
  // roughly constant across the window.
  //
  // F5: estimate G from BOTH real-price anchors — entry vs the continuous near
  // entry, and exit vs the continuous near exit — then average. Each anchor is
  // time-matched to its own real price, so a trending window does not bias it,
  // and averaging HALVES the error when one end sits behind a data gap or a bad
  // boundary tick, which the old single-anchor `bars[0]` absorbed in full.
  const gEntry = first.o - p.entry;
  const exitN = Number(p.exit);

  // F7 interaction: only trust the exit anchor when the exit TIME is reliable.
  // On an estimatedEnd window, bars[last] is minutes past the true exit, so its
  // close reflects post-exit drift rather than the price AT the exit. Using it
  // would bias the offset and could even trip a false anchor-disagreement.
  // 🚨 THE EXIT ANCHOR IS ONLY MEANINGFUL WHEN A SHIFT IS ACTUALLY APPLIED.
  // In exact mode `shift` is 0 (see below): the offset is computed and then
  // never used. Averaging in the exit anchor, and rejecting when the two
  // anchors disagree, both exist to decide whether the two can be combined into
  // a TRUSTWORTHY SHIFT — a question that does not arise when there is no shift.
  //
  // Measured 2026-08-19: an exact-contract trade with a PERFECT entry anchor
  // (gEntry = 0) and an exit 200pt from the last bar's close was rejected
  // outright as CONTRACT_MISMATCH, and told the user the bars sat "96pt off the
  // entry ... beyond a plausible contract-roll gap" — where 96 was the average
  // of one exact anchor and one irrelevant one, and no roll correction was in
  // play at all. Any trade whose recorded exit sits more than the cap from the
  // last bar's close lost its excursion entirely.
  //
  // ⚠ WHAT THIS MUST NOT WEAKEN, and does not: if EXACT bars are for the wrong
  // instrument, `gEntry` alone is wildly off and `rollOffsetExceedsCap` below
  // still rejects them. That protection is the one that matters here, and it
  // survives untouched — only the anchor-AGREEMENT test is scoped to the mode
  // where a shift is genuinely computed.
  const gExit =
    !p.isExact && !p.estimatedEnd && isFinite(exitN) && exitN > 0 ? last.c - exitN : null;

  const offset = gExit != null ? (gEntry + gExit) / 2 : gEntry;

  // Reject an offset too large to be a genuine roll of THIS instrument, and
  // reject when the two anchors DISAGREE beyond the cap — an inconsistent
  // window whose average would be a plausible-but-wrong shift. Skipping is the
  // correct outcome: a fabricated shift produces MAE/MFE that look real.
  //
  // ⚠ F5 IS UNTOUCHED IN PROXY MODE. Averaging two disagreeing anchors produces
  // a plausible-but-wrong shift, and that reasoning holds wherever a shift is
  // applied. `gExit` is simply null in exact mode now, so this reads exactly as
  // it always did for the continuous path.
  const maxFrac = rollMaxFrac(p.symbol);
  const anchorsDisagree = gExit != null && Math.abs(gEntry - gExit) > p.entry * maxFrac;

  if (rollOffsetExceedsCap(offset, p.entry, p.symbol) || anchorsDisagree) {
    return {
      error:
        `Bars (~${Math.round(first.o)}) sit ${Math.round(Math.abs(offset))}pt off ` +
        `the entry (${p.entry})${anchorsDisagree ? ' with inconsistent entry/exit anchors' : ''}` +
        ` — beyond a plausible contract-roll gap; excursion skipped.`,
      errorCode: 'CONTRACT_MISMATCH',
      offset, gEntry, gExit,
    };
  }

  // Shift into the trade's price space on a COPY. Exact contract → already in
  // that space (shift 0). `ts` is preserved for time-to-extreme.
  const shift = p.isExact ? 0 : offset;
  const adj: Bar[] = bars.map((b) => ({
    ts: b.ts, o: b.o - shift, h: b.h - shift, l: b.l - shift, c: b.c - shift,
  }));

  // F11: a zero or negative exit is bad data. It must not drive the early-stop
  // or the realized-P&L clamp — an exit of 0 would floor MAE up to the entire
  // entry price.
  const exitVal = Number(p.exit);
  const hasValidExit = isFinite(exitVal) && exitVal > 0;

  let mfeBar = adj[0] as Bar;
  let maeBar = adj[0] as Bar;
  let walked = 0;

  for (const b of adj) {
    walked++;
    if (p.side === 'long') {
      // Strict `>` / `<` keeps the EARLIEST occurrence of each extreme, which is
      // what time-to-MFE and time-to-MAE report.
      if (b.h > mfeBar.h) mfeBar = b;
      if (b.l < maeBar.l) maeBar = b;
    } else {
      if (b.l < mfeBar.l) mfeBar = b;
      if (b.h > maeBar.h) maeBar = b;
    }

    // F7: on a proxy window, stop where price FIRST reaches the exit level, in
    // the direction it moved to get there. This is MONOTONIC — robust to a bar
    // that gaps through the level, which a straddle check (`l ≤ exit ≤ h`)
    // never fires on, silently re-including the rest of the proxy window. It is
    // also correct for losers, unlike the old side-based test which fired on
    // bar 0 for every loser and truncated the walk to a single bar.
    if (
      p.estimatedEnd && hasValidExit &&
      (exitVal >= p.entry ? b.h >= exitVal : b.l <= exitVal)
    ) break;
  }

  const mfeRaw = p.side === 'long' ? mfeBar.h - p.entry : p.entry - mfeBar.l;
  const maeRaw = p.side === 'long' ? p.entry - maeBar.l : maeBar.h - p.entry;

  // Stored as absolute magnitudes; never negative.
  let mfePoints = Math.max(0, mfeRaw);
  let maePoints = Math.max(0, maeRaw);

  // The realized-P&L invariant: MFE ≥ realized profit and MAE ≥ realized
  // adverse, because price provably reached the exit level. Gaps or an
  // estimated window can leave the walk short; the clamp floors it.
  //
  // F6: record whether the clamp MATERIALLY moved a value. A clamped result
  // means a suspect or incomplete window, and that must be surfaceable rather
  // than silently absorbed into a number that looks fine.
  let clampMfe = 0;
  let clampMae = 0;
  if (hasValidExit) {
    const realizedPoints = p.side === 'long' ? exitVal - p.entry : p.entry - exitVal;
    if (isFinite(realizedPoints) && realizedPoints > mfePoints) {
      clampMfe = realizedPoints - mfePoints;
      mfePoints = realizedPoints;
    }
    const realizedAdverse = p.side === 'long' ? p.entry - exitVal : exitVal - p.entry;
    if (isFinite(realizedAdverse) && realizedAdverse > maePoints) {
      clampMae = realizedAdverse - maePoints;
      maePoints = realizedAdverse;
    }
  }
  const clamped = clampMfe > CLAMP_EPSILON || clampMae > CLAMP_EPSILON;

  return {
    // F9: rounded at the boundary, not at each step — one place to reason about,
    // and the clamp comparisons above stay exact.
    maePoints: roundPoints(maePoints),
    mfePoints: roundPoints(mfePoints),
    // 🚨 A CLAMPED EXTREME HAS NO TIMESTAMP, BECAUSE IT WAS NEVER OBSERVED.
    // The clamp above replaces the POINTS with the realized figure — inferred
    // from the exit price, not seen in any bar. `maeBar`/`mfeBar` still point at
    // the SMALLER extreme the walk actually found, so keeping their time pairs a
    // number with the clock reading of a different event.
    //
    // Measured 2026-08-19: a window topping at +5 (t=120s) against a realized
    // +10 returned `mfePoints: 10, mfeSecs: 120` — "best move +10, reached 120s
    // in", where +10 never happened and 120s belongs to +5.
    //
    // 🔴 AND IT LANDS ON THE WORST POSSIBLE POPULATION. On a loser it is the MAE
    // clamp that fires, and `excursionDiagnose` reads LOSERS ONLY, keying its
    // whole Favorable-First verdict on `mfeSecs < maeSecs`. Peak Capture
    // reconstructs position size AT `mfeSecs`, so a stale time sizes the wrong
    // moment, and give-back shares that reconstruction.
    //
    // ▶ Null is the honest answer, and the same rule the ATAS logger's timing
    // guard adopted: blank beats confidently wrong. Every consumer already
    // null-checks these — `excursionDiagnose` tests `!= null` FIRST, precisely
    // because `Number(null)` is 0 and would read as "peaked at second zero".
    //
    // ⚠ PER SIDE, not both: only the extreme that was actually clamped loses its
    // time. `clampInfo` records which, and the untouched side keeps a real one.
    maeSecs: clampMae > CLAMP_EPSILON
      ? null : Math.max(0, Math.round((maeBar.ts - p.startMs) / 1000)),
    mfeSecs: clampMfe > CLAMP_EPSILON
      ? null : Math.max(0, Math.round((mfeBar.ts - p.startMs) / 1000)),
    excursionTz: p.tz,
    clamped,
    clampInfo: clamped ? { mfe: clampMfe, mae: clampMae } : null,
    bars: walked,
    rollOffsetApplied: shift,
  };
}

// =============================================================================
// The BAR-FREE floor clamp — the same invariant, applied at READ time.
// -----------------------------------------------------------------------------
// `computeExcursionWalk` above clamps when it COMPUTES an excursion from bars.
// This pair applies the identical invariant to values ALREADY STORED, with no
// bars involved, and it is a separate application point on purpose.
//
// ⚠ THE OLD CLIENT RUNS THIS ON EVERY LOAD (`migrateState`, audit item F9) and
// marks the trade `excursionClamped`. So `mae_points` / `mfe_points` are a CACHE
// the live app corrects before showing — exactly like `session_date` and `fees`
// (D59). A client that renders the raw columns under-reports MAE on losers and
// MFE on winners.
//
// That is the flattering direction: a smaller MAE reads as a tighter, better
// managed trade than the one actually taken.
//
// Why stored values can be below their floor at all: the walk is over a bar
// WINDOW, and the window can miss — a wrong-timezone window, a gap in the feed,
// a contract roll. The floor needs no window, so it can always correct one.
// =============================================================================

export interface ExcursionFloors {
  /** How far price provably went AGAINST the trade. `0` on a winner. */
  adverse: number;
  /** How far price provably went IN FAVOUR. `0` on a loser. */
  profit: number;
}

/**
 * The floors implied by entry, exit and side — or null if the inputs are unusable.
 *
 * ⚠ The `<= 0` price guard is load-bearing (F11). With `entry = 0`, `profit`
 * becomes `exit - 0` — the whole exit price — and the clamp would raise MFE to
 * it on every load. Bad data is rejected here rather than propagated as an
 * enormous excursion.
 */
export function realizedExcursionFloors(
  entry: number | null | undefined,
  exit: number | null | undefined,
  side: string | null | undefined,
): ExcursionFloors | null {
  const e = Number(entry);
  const x = Number(exit);
  if (!isFinite(e) || !isFinite(x) || e <= 0 || x <= 0) return null;
  if (side !== 'long' && side !== 'short') return null;
  const adverse = side === 'long' ? e - x : x - e;
  const profit = side === 'long' ? x - e : e - x;
  return { adverse: Math.max(0, adverse), profit: Math.max(0, profit) };
}

export interface FloorClampResult {
  maePoints: number | null;
  mfePoints: number | null;
  clamped: boolean;
}

/**
 * Raise stored MAE/MFE to their floors where the stored value is impossible.
 *
 * ⚠ ONLY RAISES. A value above its floor is left alone — the floor is a minimum,
 * not the answer. Price may well have gone further than the realized move.
 *
 * ⚠ NULLS PASS THROUGH. "No excursion computed yet" is a different fact from "no
 * excursion", and deriving one from the realized move would fabricate data for
 * every trade whose bars were never walked.
 *
 * ⚠ `+ CLAMP_EPSILON` before comparing, as in the walk. Without it a value equal
 * to its floor trips on float noise, marking a correct trade clamped on every
 * load — and in the old client, writing it back each time.
 */
export function excursionFloorClamp(
  maePoints: number | null | undefined,
  mfePoints: number | null | undefined,
  entry: number | null | undefined,
  exit: number | null | undefined,
  side: string | null | undefined,
): FloorClampResult {
  let mae = maePoints ?? null;
  let mfe = mfePoints ?? null;
  let clamped = false;

  const fl = realizedExcursionFloors(entry, exit, side);
  if (fl) {
    // ⚠ `!= null`, not truthiness. A stored 0 is a real measurement — and it is
    // precisely what a missed window yields on a loser, so it is the case this
    // most needs to catch.
    if (fl.adverse > 0 && mae != null && Number(mae) + CLAMP_EPSILON < fl.adverse) {
      mae = fl.adverse;
      clamped = true;
    }
    if (fl.profit > 0 && mfe != null && Number(mfe) + CLAMP_EPSILON < fl.profit) {
      mfe = fl.profit;
      clamped = true;
    }
  }

  return { maePoints: mae, mfePoints: mfe, clamped };
}
